import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Routes, Route, useNavigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom'
import { Snackbar, Alert, Button, Box, Typography, Tabs, Tab } from '@mui/material'
import { Page, PageTitle } from '../../components'
import useFetch from '../../hooks/useFetch'
import { slotDisplayName } from '../photo-upload/PhotoUpload'
import ReviewGrid from './ReviewGrid'
import PieceReview from './PieceReview'
import PublishList from './PublishList'
import PiecePreview from './PiecePreview'
import { getQueue, reroll, reeditWithPrompt, reshadow, colorMatch } from './photoReviewApi'
import { queueIsBusy } from './reviewModel'

const REEDIT_POLL_MS = 5000

// Module level, not inside PhotoReview: a component declared in render is a new
// type every render, so each queue refresh would remount the piece and close an
// open prompt drawer.
function PieceRoute({ items, onReroll, onReeditWithPrompt, onReshadow, onColorMatch, ...rest }) {
    const { sku } = useParams()
    const item = items.find(candidate => candidate.sku === sku)
    return (
        <PieceReview
            item={item}
            onReroll={(slot, referencePhotoId) => onReroll(sku, slot, referencePhotoId)}
            onReeditWithPrompt={(slot, prompt, referencePhotoId) => onReeditWithPrompt(sku, slot, prompt, referencePhotoId)}
            onReshadow={(slot, settings) => onReshadow(sku, slot, settings)}
            onColorMatch={(slot, masks) => onColorMatch(sku, slot, masks)}
            {...rest}
        />
    )
}

// Owns everything that must outlive a view change: the queue, and the re-edits
// still running. Grid and piece are nested routes of this one component, so
// leaving a piece mid re-edit keeps the request and its notice alive.
export default function PhotoReview({ auth, setAuth }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    // Persisted: re-edits take long enough that the reviewer has looked away
    // by the time a failure comes back.
    const { fetchResource, isPending, showAlert, alertMessage, setShowAlert } = useFetch({ setAuth, persistAlerts: true })
    const [items, setItems] = useState(null)
    const [loadFailed, setLoadFailed] = useState(false)
    // photoId -> the `attempts` its re-edit started from (see isRegenerating).
    const [inFlight, setInFlight] = useState(() => new Map())
    const [notices, setNotices] = useState([])
    // What Cancel reopen did, said on the grid the piece has just left.
    const [pageMessage, setPageMessage] = useState(null)
    // The last good queue, so a failed refetch answers with what is on screen
    // rather than an empty list that would bounce the piece view to the grid.
    const itemsRef = useRef([])

    const refresh = useCallback(async () => {
        const response = await getQueue(fetchResource)
        if (response.error) {
            setLoadFailed(true)
            return itemsRef.current
        }
        setLoadFailed(false)
        itemsRef.current = response.items
        setItems(response.items)
        return response.items
    }, [fetchResource])

    useEffect(() => { refresh() }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // While any re-edit runs, the queue is re-read every few seconds, so a new
    // version shows the moment it is stored, even if the request answers late
    // or never (a proxy timeout on a long edit). Likewise while the server is
    // editing or has queued a shot (process-now runs in the background).
    const polling = inFlight.size > 0 || queueIsBusy(items)
    useEffect(() => {
        if (!polling) return undefined
        const timer = setInterval(refresh, REEDIT_POLL_MS)
        return () => clearInterval(timer)
    }, [polling, refresh])

    async function runReedit(sku, slot, request) {
        setInFlight(prev => new Map(prev).set(slot.photoId, slot.attempts))
        const response = await request()
        // Only this re-edit's own entry: once polling showed its version, a
        // second re-edit of the same shot may have started from that one.
        setInFlight(prev => {
            if (prev.get(slot.photoId) !== slot.attempts) return prev
            const next = new Map(prev)
            next.delete(slot.photoId)
            return next
        })
        const values = { sku, slot: slotDisplayName(t, slot.slot) }
        setNotices(prev => [...prev, { id: `${slot.photoId}-${Date.now()}`, sku, ok: !response.error, values }])
        refresh()
    }

    // Open a reopened piece once the queue is re-read. A reshoot, or a review
    // with every shot approved, is not in the queue: it waits in Ready to publish.
    async function handleReopened(sku) {
        setPageMessage(null)
        const queue = await refresh()
        const inReview = queue.some(item => item.sku === sku)
        navigate(inReview ? `/photo-review/${sku}` : `/photo-review/publish/${sku}`)
    }

    // Every shot is back as published, so the piece is no longer in review
    // nor ready: back to the list it was opened from.
    function handleReopenCancelled(sku, list = '/photo-review') {
        setPageMessage(t('photoReview.republish.cancelled', { sku }))
        navigate(list)
        refresh()
    }

    const notice = notices[0]
    // The final per-piece approval ("Prontas para publicar") is its own tab.
    const tab = useLocation().pathname.startsWith('/photo-review/publish') ? 'publish' : 'review'

    return (
        <Page>
            <PageTitle>{t('photoReview.title')}</PageTitle>
            <Tabs value={tab} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab value='review' label={t('photoReview.publish.tabs.review')} component={RouterLink} to='/photo-review' />
                <Tab value='publish' label={t('photoReview.publish.tabs.ready')} component={RouterLink} to='/photo-review/publish' />
            </Tabs>
            {showAlert && (
                <Alert severity={alertMessage.type || 'error'} variant='filled' onClose={() => setShowAlert(false)} sx={{ mb: 2 }}>
                    {alertMessage.message}
                </Alert>
            )}
            {pageMessage && (
                <Alert severity='success' onClose={() => setPageMessage(null)} sx={{ mb: 2 }}>{pageMessage}</Alert>
            )}
            {items === null && loadFailed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography color='text.secondary'>{t('photoReview.loadFailed')}</Typography>
                    <Button variant='outlined' onClick={refresh}>{t('photoReview.tryAgain')}</Button>
                </Box>
            )}
            <Routes>
                {/* Not tied to the review queue: they load their own data. */}
                <Route path='publish' element={<PublishList fetchResource={fetchResource} />} />
                {/* PHOTOR is the admin perm (role 2 or below), the tier the approve route needs. */}
                <Route path='publish/:sku' element={<PiecePreview fetchResource={fetchResource} onPublished={refresh} onSentBack={refresh} onReopenCancelled={sku => handleReopenCancelled(sku, '/photo-review/publish')} canPublish={Boolean(auth?.perms?.PHOTOR)} />} />
                {items !== null && (
                    <>
                        <Route index element={<ReviewGrid items={items} fetchResource={fetchResource} isPending={isPending} onChanged={refresh} onReopened={handleReopened} />} />
                        <Route path=':sku' element={(
                            <PieceRoute
                                items={items}
                                fetchResource={fetchResource}
                                inFlight={inFlight}
                                onReroll={(sku, slot, referencePhotoId) => runReedit(sku, slot, () => reroll(fetchResource, slot.photoId, referencePhotoId))}
                                onReeditWithPrompt={(sku, slot, prompt, referencePhotoId) => runReedit(sku, slot, () => reeditWithPrompt(fetchResource, slot.photoId, prompt, referencePhotoId))}
                                onReshadow={(sku, slot, settings) => runReedit(sku, slot, () => reshadow(fetchResource, slot.photoId, settings))}
                                onColorMatch={(sku, slot, masks) => runReedit(sku, slot, () => colorMatch(fetchResource, slot.photoId, masks))}
                                onChanged={refresh}
                                onReopenCancelled={handleReopenCancelled}
                            />
                        )} />
                    </>
                )}
            </Routes>
            {/* Keyed per notice so each gets its own timer, and deaf to click-away: the
                reviewer keeps working while a notice waits, and its link is the
                only way back to the re-edited piece. */}
            <Snackbar
                key={notice?.id}
                open={Boolean(notice)}
                autoHideDuration={8000}
                onClose={(_, reason) => { if (reason !== 'clickaway') setNotices(prev => prev.slice(1)) }}
            >
                {notice ? (
                    <Alert
                        severity={notice.ok ? 'success' : 'error'}
                        onClose={() => setNotices(prev => prev.slice(1))}
                        action={<Button color='inherit' size='small' onClick={() => { setNotices(prev => prev.slice(1)); navigate(`/photo-review/${notice.sku}`) }}>{t('photoReview.notice.open')}</Button>}
                    >
                        {t(notice.ok ? 'photoReview.notice.done' : 'photoReview.notice.failed', notice.values)}
                    </Alert>
                ) : <span />}
            </Snackbar>
        </Page>
    )
}
